import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { manualTransactions } from '../../util/api';
import { userTypeOf } from '../../util/userType';
import { showPostingListing } from '../../util/postingRead';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/CalendarPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/CalendarPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/CalendarPage/FETCH_ORDERS_OR_SALES_ERROR';

export const FETCH_ALL_TRANSACTIONS_REQUEST = 'app/CalendarPage/FETCH_ALL_TRANSACTIONS_REQUEST';
export const FETCH_ALL_TRANSACTIONS_SUCCESS = 'app/CalendarPage/FETCH_ALL_TRANSACTIONS_SUCCESS';
export const FETCH_ALL_TRANSACTIONS_ERROR = 'app/CalendarPage/FETCH_ALL_TRANSACTIONS_ERROR';

export const SET_LOADING_STATE = 'app/CalendarPage/SET_LOADING_STATE';
export const SET_MAIN_ADMIN_ID = 'app/CalendarPage/SET_MAIN_ADMIN_ID';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  users: [],
  pendingUsers: [],
  scheduledTxns: null,
  loading: false,
  profileListingAdminId: null,
};

export default function userManagementPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };
    
    case FETCH_ALL_TRANSACTIONS_REQUEST:
      return { ...state, getAllTransactionsInProgress: true, getAllTransactionsError: null };
    case FETCH_ALL_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        getAllTransactionsInProgress: false,
        scheduledTxns: payload,
      };
    case FETCH_ALL_TRANSACTIONS_ERROR:
      console.error(payload);
      return { ...state, getAllTransactionsInProgress: false, getAllTransactionsError: payload };

    case SET_LOADING_STATE:
      return {
        ...state,
        loading: payload,
      };

    case SET_MAIN_ADMIN_ID: 
      return {
        ...state,
        profileListingAdminId: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

export const fetchAllTransactionsRequest = () => ({ type: FETCH_ALL_TRANSACTIONS_REQUEST });
export const fetchAllTransactionsSuccess = (payload) => ({
  type: FETCH_ALL_TRANSACTIONS_SUCCESS,
  payload: payload,
});
export const fetchAllTransactionsError = (e) => ({
  type: FETCH_ALL_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

export const setLoadingState = (payload) => ({
  type: SET_LOADING_STATE,
  payload: payload,
});

export const setMainAdminId = (id) => ({
  type: SET_MAIN_ADMIN_ID,
  payload: id,
});

// ================ Thunks ================ //

export const getMainAccountId = async (user, sdk) => {
  if (!user) return null;

  if (userTypeOf(user) === 'candidate') {
    return user.id?.uuid;
  } else {
    let profileListing = await sdk.listings.show({ id: user?.attributes?.profile?.publicData?.profileListingId });
    profileListing = profileListing?.data?.data;

    return profileListing?.attributes?.publicData?.adminId;
  }  
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  // return true;
  dispatch(setLoadingState(true));
  dispatch(fetchAllTransactionsRequest());
  
  const { currentUser } = getState().user;
  const currentUserAdminId = await getMainAccountId(currentUser, sdk);
  dispatch(setMainAdminId(currentUserAdminId));

  return manualTransactions({ userAId: currentUserAdminId, txType: 'apply', status: 'scheduled', type:'fetch' })
    .then(async response => {

      let allTxns = response;

      // Deduplicate listing IDs to avoid redundant API calls
      const uniqueListingIds = [...new Set(allTxns?.map(txn => txn?.listingId).filter(Boolean))];

      const listingsMap = {};
      await Promise.all(
        uniqueListingIds.map(async id => {
          const res = await showPostingListing(sdk, { id });
          listingsMap[id] = res?.data?.data;
        })
      );

      allTxns = allTxns.map(txn => ({
        ...txn,
        listing: listingsMap[txn?.listingId] || null,
      }));

      dispatch(fetchAllTransactionsSuccess(allTxns));
      dispatch(setLoadingState(false));

      return response;
    })
  .catch(e => {
    dispatch(setLoadingState(false));
    // dispatch(fetchOrdersOrSalesError(storableError(e)));
    dispatch(fetchAllTransactionsError(storableError(e)));
    throw e;
  });
};

